// src/components/wellfinder/RecordDetail.jsx
//
// Map left, candidates right at equal height; record facts and history full
// width beneath. Candidates sit BESIDE the map rather than under it because the
// worst case is twelve of them, and twelve stacked cards push the imagery off
// screen exactly when the analyst most needs to cross-reference the two.
//
// ── SIZING: A FLOOR, NOT A FIXED SIZE ────────────────────────────────────────
// Fixed zoom fixes metres per pixel, so a 5m offset renders identically at any
// container size. What a resizing container changes is COVERAGE. The floor is
// therefore about how much ground is in frame, not about keeping the scale
// honest — an earlier note in this file had that backwards.
//
// At MapLibre z18 and 31.7N the scale is 0.254 m/px (worldSize = 512 * 2^zoom,
// not 256 — see reviewMapEncoding.js). So:
//
//   pane px   metres across   radius covered
//   -------   -------------   --------------
//      480         121.9          +/- 61 m
//      560         142.3          +/- 71 m
//      790         200.7          +/- 100 m   <- full candidate radius
//
// Measured against the corpus, the farthest candidate per record is p50 16.5m,
// p90 71.7m, max 100.0m. A +/-71m frame fully contains 9,546 of 10,639 records
// with candidates; +/-100m contains all of them. The 1,093 that overflow do so
// only in their FARTHEST candidate, and a candidate whose link line runs off
// the edge still announces itself — the line is the cue that there is more
// outside the frame.
//
// Floors below are 480px, which is what a 40%-wide right pane leaves on a
// 1280px window. Guaranteeing +/-100m at every window size would need a 790px
// square, which is more vertical than a laptop has. Reported rather than
// silently chosen: if the 1,093 partial frames matter, the answer is a bigger
// minimum height, not a different zoom.

import LeaseName from "./LeaseName";
import { useCallback } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useWfQueueStore } from "../../stores/useWfQueueStore";
import ReviewMap from "./ReviewMap";
import CandidatePanel from "./CandidatePanel";
import HolderChip from "./HolderChip";
import RecordFacts from "./RecordFacts";
import VerdictBar from "./VerdictBar";
import UndoToast from "./UndoToast";
import { defaultZoomFor } from "./reviewMapEncoding";

export default function RecordDetail() {
  const detail = useWfQueueStore((s) => s.detail);
  const loading = useWfQueueStore((s) => s.detailLoading);
  const error = useWfQueueStore((s) => s.detailError);
  const selectedId = useWfQueueStore((s) => s.selectedId);
  const hoveredRank = useWfQueueStore((s) => s.hoveredRank);
  const selectedRank = useWfQueueStore((s) => s.selectedRank);
  const hoverCandidate = useWfQueueStore((s) => s.hoverCandidate);
  const selectCandidate = useWfQueueStore((s) => s.selectCandidate);
  const imageryError = useWfQueueStore((s) => s.imageryError);
  const setImageryError = useWfQueueStore((s) => s.setImageryError);
  const footprintVisible = useWfQueueStore((s) => s.footprintVisible);
  const toggleFootprint = useWfQueueStore((s) => s.toggleFootprint);
  const geomMode = useWfQueueStore((s) => s.geomMode);
  const geomDraft = useWfQueueStore((s) => s.geomDraft);
  const boundaryMode = useWfQueueStore((s) => s.boundaryMode);
  const boundaryDraft = useWfQueueStore((s) => s.boundaryDraft);
  const setBoundaryDraft = useWfQueueStore((s) => s.setBoundaryDraft);
  const zoomOverride = useWfQueueStore((s) => s.zoomOverride);
  const setGeomDraft = useWfQueueStore((s) => s.setGeomDraft);
  const holderRank = useWfQueueStore((s) => s.holderRank);
  const toggleHolder = useWfQueueStore((s) => s.toggleHolder);
  const openTransfer = useWfQueueStore((s) => s.openTransfer);

  // Hover wins while the pointer is over something, otherwise the sticky
  // selection shows. One value reaches the map so the paint expressions stay
  // single-valued.
  const activeRank = hoveredRank ?? selectedRank;

  // Imagery failure does NOT take the record down with it. The analyst can
  // still read candidates, distances and history; what they cannot do is judge
  // the detection, and the overlay says exactly that rather than leaving blank
  // tiles that look like a slow load.
  const onImageryError = useCallback((err) => setImageryError(err), [setImageryError]);

  if (!selectedId) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="max-w-xs text-center text-sm text-fg-secondary">
          Select a record from the queue to review it.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="max-w-md text-center text-sm text-red-400">
          Could not load this record: {error.message}
        </p>
      </div>
    );
  }

  // `detail?.record`, not `detail`. A response that is not a detail payload —
  // a shape change, a proxy returning something else — must render as "not
  // loaded" and not throw: an exception here unmounts the whole page, including
  // the queue the analyst was navigating.
  if (!detail?.record) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-sm text-fg-secondary">
          {loading ? "loading record…" : "No record loaded."}
        </p>
      </div>
    );
  }

  const r = detail.record;
  const img = detail.imagery;

  // The candidate that IS the record's match, by rrc_id; null when none is.
  const adopted = r.rrc_id ? detail.candidates?.find((c) => c.rrc_id === r.rrc_id) ?? null : null;

  // The taken candidate whose holder is on the map. Derived from the loaded
  // candidates rather than stored, so a background refresh that finds the
  // well released simply stops drawing it — is_taken is the gate.
  const holderCand = holderRank == null
    ? null
    : detail.candidates?.find((c) => c.rank === holderRank && c.is_taken) ?? null;
  const holderPoint = holderCand && holderCand.taken_by_lon != null
    ? {
      lon: holderCand.taken_by_lon, lat: holderCand.taken_by_lat,
      wellLon: holderCand.rrc_lon, wellLat: holderCand.rrc_lat,
    }
    : null;

  return (
    // ── THE COMMAND BAR IS A ROW, NOT AN OVERLAY ────────────────────────────
    // It was `sticky bottom-0` INSIDE the scroll container, so it painted over
    // the facts block — and at scroll-top, before the analyst had scrolled at
    // all, api10 and the attribute lines were already behind it.
    //
    // padding-bottom on the scroller equal to the bar height is the cheaper fix
    // and it is wrong twice: the bar still floats over content while scrolling,
    // and the padding silently stops matching the moment the bar grows a line —
    // which it does, on every commit error and whenever the reject picker opens.
    //
    // So: this container does NOT scroll. It is a three-row column — header,
    // scroll region, bar — and the scroll region ends at the bar's top edge
    // because they are siblings. The bar cannot overlay what it is laid out
    // beside, at any height, without anyone maintaining a number.
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-bg-border px-4 py-2">
        <div className="flex items-baseline gap-3">
          {/* THE ADOPTED WELL, OR WHY THERE IS NONE. This named candidate 1
              whatever was adopted, so a tier B record nobody had matched read
              as rank 1's well, and a record matched to rank 2 read as rank 1. */}
          <h2 className="truncate text-sm font-medium text-fg-primary">
            {adopted
              ? (
                <>
                  <LeaseName value={adopted.rrc_lease_name} source={adopted.rrc_lease_name_source} />
                  {adopted.rrc_well_no ? ` ${adopted.rrc_well_no}` : ""}
                </>
              )
              : detail.candidates?.length
                ? (r.status === "approved" ? "No matching RRC record" : "No candidate adopted")
                : "Detection with no registry candidate"}
          </h2>
          <span className="shrink-0 text-[11px] text-fg-secondary">
            {r.detection_source_id ?? r.source_id}
          </span>
        </div>
        {img ? (
          // Which imagery the analyst is looking at, because "can you identify
          // a wellhead here" is not answerable without knowing the LOD and the
          // date — and now the PRODUCT. 1,005 of these records were rendering
          // NDVI, and nothing on screen said so; an incorrect selection has to
          // be visible in the UI rather than inferable only from the pixels
          // looking odd.
          <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-fg-secondary/70">
            <span
              data-testid="imagery-product-class"
              className={[
                "rounded px-1 text-[10px]",
                img.product_class === "visual"
                  ? "bg-white/[0.06] text-fg-secondary"
                  // Loud on purpose. The resolver should never hand a derived
                  // product to this view, so if one appears the analyst must
                  // not quietly adjudicate from it.
                  : "bg-accent-red/20 text-accent-red",
              ].join(" ")}
            >
              {img.product_class === "visual" ? "visual" : `${img.product_class} — not imagery`}
            </span>
            <span>{img.sku_code}</span>
            <span>· {img.dataset_key}</span>
            <span>· {img.lod}</span>
            <span>· {img.acquisition_date}</span>
          </p>
        ) : (
          // Null is now reachable in principle: no entitled VISUAL dataset
          // covers this point. It does not occur on today's corpus — all 10,803
          // resolve — but the caller renders the true statement rather than an
          // approximation of it.
          <p className="mt-0.5 text-[11px] text-accent-red">
            No visual imagery covers this location — the map is showing the
            basemap only. Do not adjudicate from it.
          </p>
        )}
      </div>

      {/* THE SCROLL REGION. Everything the analyst reads; nothing they act
          with. Ends at the command bar's top edge by construction. */}
      <div data-testid="detail-scroll" className="min-h-0 flex-1 overflow-y-auto">

      {/* Map + candidates, equal height, ~60/40. */}
      {/* HEIGHT CLAMPED TO THE VIEWPORT, FLOOR INTACT.
          Item 1 stops the command bar OVERLAYING the map, which is what was
          clipping the legend. It does not stop a fixed 560px row falling below
          the scroll fold on a short screen: at an 800px viewport the header and
          the bar leave ~540px, so the last 20px of the map — and the legend
          sitting in it — needed a scroll to reach.
          min(560px, max(480px, 52vh)) yields 480px at 800px, 560px at 1200px,
          and never goes under the 480px coverage floor. Width is untouched, so
          metres-across is unchanged; what varies with height is vertical
          coverage, between the +/-61m floor and +/-71m, both already reported. */}
      <div
        data-testid="detail-upper"
        className="flex h-[min(560px,max(480px,52vh))] shrink-0 border-b border-bg-border"
      >
        <div
          data-testid="map-pane"
          className="relative min-h-[480px] min-w-[480px] flex-[3] border-r border-bg-border"
        >
          <ReviewMap
            detail={detail}
            activeRank={activeRank}
            footprintVisible={footprintVisible}
            onHoverCandidate={hoverCandidate}
            onSelectCandidate={selectCandidate}
            onImageryError={onImageryError}
            imageryError={imageryError}
            geomMode={geomMode}
            geomDraft={geomDraft}
            onGeomDraft={setGeomDraft}
            boundaryMode={boundaryMode}
            boundaryDraft={boundaryDraft}
            onBoundaryDraft={setBoundaryDraft}
            zoom={zoomOverride ?? defaultZoomFor(detail)}
            holder={holderPoint}
          />
          {/* Top-left is the move-mode banner's place while that mode is on;
              the banner is the more urgent of the two, so the chip yields. */}
          {holderCand && !geomMode && (
            <HolderChip
              candidate={holderCand}
              onHide={() => toggleHolder(holderCand.rank)}
              // Only where this record can still take a verdict. An approved
              // record would have to be reopened first; offering the move
              // there would open a confirmation that cannot be sent.
              onMove={r.status === "pending" || r.status === "needs_review"
                ? () => openTransfer(holderCand.rank)
                : null}
            />
          )}
        </div>

        {/* flex-[2] and min-w-0 so the pane can actually shrink — a flex child
            defaults to min-width:auto and would otherwise refuse to go below
            its content width, pushing the map narrower than its floor. */}
        <section
          data-testid="candidate-pane"
          className="flex min-h-0 min-w-0 flex-[2] flex-col"
        >
          <div className="flex shrink-0 items-center gap-2 px-3 pt-3 pb-1.5">
            <h3 className="text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
              Candidates{" "}
              {detail.candidates?.length ? `(${detail.candidates.length})` : ""}
            </h3>
            {/* One toggle, and only this one. Whether ~62 m² of outline inside a
                ~142m frame helps or clutters is a usage question, so it is
                settled by usage rather than guessed at now. Same eye/eye-off
                affordance as PadRekko's per-finding visibility. */}
            <button
              type="button"
              onClick={toggleFootprint}
              aria-pressed={footprintVisible}
              aria-label={footprintVisible ? "Hide wellhead footprint" : "Show wellhead footprint"}
              title={footprintVisible ? "Hide wellhead footprint" : "Show wellhead footprint"}
              data-testid="footprint-toggle"
              className={[
                "ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ring-1 transition-colors",
                footprintVisible
                  ? "bg-white/[0.06] text-fg-secondary ring-white/15"
                  : "text-fg-secondary/50 ring-white/10 hover:bg-white/[0.04]",
              ].join(" ")}
            >
              {footprintVisible ? <Eye size={11} /> : <EyeOff size={11} />}
              footprint
            </button>
          </div>
          {/* Scrolls WITHIN the pane. Twelve candidates is the corpus worst
              case and must not be able to push the map out of view. */}
          <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
            <CandidatePanel
              candidates={detail.candidates}
              hoveredRank={hoveredRank}
              selectedRank={selectedRank}
              onHover={hoverCandidate}
              onSelect={selectCandidate}
              holderRank={holderRank}
              onToggleHolder={toggleHolder}
            />
          </div>
        </section>
      </div>

      {/* Facts and history, full width beneath both, and NOT internally
          scrollable — the floor is what the two sections need to render whole.
          Event history grows as adjudication proceeds, so this is a minimum and
          not a height: the block expands and the scroll region absorbs it. */}
      <div data-testid="facts-pane" className="min-h-[420px] shrink-0 p-4">
        <RecordFacts detail={detail} />
      </div>

      </div>

      {/* Its own row. Always visible because it is laid out, not because it is
          pinned over something.
          `relative` so the undo toast can anchor to the TOP of this row rather
          than to the viewport bottom, where it landed on the commit buttons.
          Moved here from page level deliberately: the trade is that a failed
          load of the NEXT record hides the affordance, and `u` still works
          because the binding is global and the target lives in the store. */}
      <div className="relative shrink-0">
        <UndoToast />
        <VerdictBar />
      </div>
    </div>
  );
}
